import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Utensils, 
  HeartHandshake, 
  Hotel, 
  Headset, 
  Clock, 
  Users, 
  TrendingUp, 
  Shield,
  CheckCircle2,
  ArrowRight,
  Calendar,
  BarChart3,
  Smartphone,
  Globe,
  Zap,
  DollarSign,
  Target,
  Award,
  FileText,
  MapPin,
  Bell,
  UserCheck,
  Building2,
  Briefcase,
  ShoppingCart,
  Factory,
  GraduationCap,
  Truck
} from 'lucide-react';
import LandingNav from '../components/Landing-Nav.js';
import FooterLanding from '../components/FooterLanding.js';

const Solutions = () => {
  const [activeIndustry, setActiveIndustry] = useState(0);
  const [selectedFeature, setSelectedFeature] = useState(null);

  // SEO-optimized industry solutions with comprehensive details
  const industries = [
    {
      icon: HeartHandshake,
      title: 'Healthcare & Medical Facilities',
      subtitle: 'Patient Care Starts with Proper Staff Management',
      description: 'Optimize staff scheduling and patient care coordination with HIPAA-compliant, secure workforce management software. Ensure your healthcare teams—nurses, doctors, technicians, and support staff—are properly staffed across all shifts.',
      image: '',
      color: 'from-red-500 to-pink-600',
      route: '/industries/healthcare',
      benefits: [
        'HIPAA-compliant scheduling and timekeeping',
        'Automatic overtime and compliance tracking',
        'Real-time shift coverage monitoring',
        'Patient-to-staff ratio optimization',
        'Credential and certification tracking',
        'Emergency on-call management'
      ],
      useCases: [
        'Hospitals & Medical Centers',
        'Nursing Homes & Assisted Living',
        'Urgent Care Clinics',
        'Home Healthcare Services',
        'Rehabilitation Centers',
        'Diagnostic Employeeatories'
      ],
      stats: {
        efficiency: '40% reduction in scheduling time',
        cost: '25% decrease in overtime costs',
        satisfaction: '95% staff satisfaction rate'
      }
    },
    {
      icon: Utensils,
      title: 'Hospitality & Food Service',
      subtitle: 'Elevate Guest Experiences with Efficient Staff Management',
      description: 'Streamline your hospitality operations with smart workforce management tools designed for fast-paced, customer-facing environments. From restaurants to event venues, manage staff scheduling, track attendance in real-time, and improve guest service while significantly reducing Employee costs.',
      image: '',
      color: 'from-orange-500 to-red-600',
      route: '/industries/hospitality',
      benefits: [
        'Dynamic scheduling based on reservations and foot traffic',
        'Mobile clock-in/out for front and back-of-house staff',
        'Tip and gratuity tracking and distribution',
        'Employee cost percentage optimization',
        'Multi-location management dashboard',
        'Peak hours and seasonal planning tools'
      ],
      useCases: [
        'Fine Dining Restaurants',
        'Quick Service Restaurants (QSR)',
        'Catering Services',
        'Bars & Nightclubs',
        'Event Venues',
        'Food Trucks & Pop-ups'
      ],
      stats: {
        efficiency: '35% faster shift planning',
        cost: '30% reduction in Employee costs',
        satisfaction: '92% employee retention'
      }
    },
    {
      icon: Hotel,
      title: 'Hotels & Accommodations',
      subtitle: 'Seamless Operations for Exceptional Guest Experiences',
      description: 'Simplify hotel staff management across all departments—housekeeping, front desk, concierge, maintenance, and kitchen teams. Shiftry\'s comprehensive workforce solution ensures every shift is properly covered, guest requests are handled promptly, and service standards remain consistently high.',
      image: '',
      color: 'from-blue-500 to-purple-600',
      route: '/industries/hotels',
      benefits: [
        'Department-specific scheduling templates',
        'Guest occupancy-based staffing',
        'Housekeeping room assignment tracking',
        'Multi-property management capabilities',
        'Seasonal demand forecasting',
        'Maintenance request coordination'
      ],
      useCases: [
        'Luxury Hotels & Resorts',
        'Budget & Economy Hotels',
        'Boutique Hotels',
        'Extended Stay Properties',
        'Vacation Rentals',
        'Bed & Breakfasts'
      ],
      stats: {
        efficiency: '45% improvement in room turnover',
        cost: '28% reduction in overtime',
        satisfaction: '98% guest satisfaction'
      }
    },
    {
      icon: Headset,
      title: 'Call Centers & Customer Support',
      subtitle: 'Maximize Agent Productivity and Customer Satisfaction',
      description: 'Boost productivity and manage agent schedules effortlessly with specialized tools for contact centers. Whether it\'s inbound customer service, outbound sales, or technical support, Shiftry makes it easy to manage shifts, track attendance, monitor performance metrics, and maintain optimal service levels.',
      image: '',
      color: 'from-green-500 to-teal-600',
      route: '/industries/call-centers',
      benefits: [
        'Service level adherence tracking',
        'Skill-based agent scheduling',
        'Break and lunch rotation management',
        'Real-time queue monitoring integration',
        'Performance metrics dashboard',
        'Remote and hybrid work support'
      ],
      useCases: [
        'Inbound Customer Service',
        'Outbound Sales Teams',
        'Technical Support Centers',
        'BPO & Outsourcing Firms',
        '24/7 Help Desks',
        'Virtual Call Centers'
      ],
      stats: {
        efficiency: '50% faster schedule creation',
        cost: '32% decrease in idle time',
        satisfaction: '94% customer satisfaction'
      }
    },
    {
      icon: ShoppingCart,
      title: 'Retail & E-commerce',
      subtitle: 'Flexible Workforce Solutions for Dynamic Retail Environments',
      description: 'Manage retail staff across multiple locations with ease. Handle peak shopping seasons, promotional events, and unexpected rushes with intelligent scheduling that adapts to real-time sales data and foot traffic patterns.',
      image: '',
      color: 'from-purple-500 to-pink-600',
      route: '/industries/retail',
      benefits: [
        'POS integration for traffic-based scheduling',
        'Seasonal hiring and onboarding workflows',
        'Store opening and closing checklists',
        'Commission and sales tracking',
        'Inventory management coordination',
        'Black Friday and holiday planning tools'
      ],
      useCases: [
        'Department Stores',
        'Specialty Retail Shops',
        'Grocery & Supermarkets',
        'Fashion & Apparel Stores',
        'Electronics Retailers',
        'Warehouse & Fulfillment Centers'
      ],
      stats: {
        efficiency: '42% improvement in coverage',
        cost: '35% reduction in Employee waste',
        satisfaction: '91% staff engagement'
      }
    },
    {
      icon: Factory,
      title: 'Manufacturing & Production',
      subtitle: 'Optimize Production Lines with Precision Workforce Planning',
      description: 'Coordinate complex shift patterns, manage production quotas, and ensure safety compliance across manufacturing facilities. Track certifications, manage equipment training, and maintain optimal production capacity with data-driven scheduling.',
      image: '',
      color: 'from-gray-600 to-blue-700',
      route: '/industries/manufacturing',
      benefits: [
        'Multi-shift rotation planning',
        'Equipment certification tracking',
        'Production quota-based staffing',
        'Safety compliance documentation',
        'Overtime and union rule management',
        'Planned downtime coordination'
      ],
      useCases: [
        'Assembly & Production Plants',
        'Food & Beverage Manufacturing',
        'Pharmaceutical Production',
        'Automotive Manufacturing',
        'Textile & Apparel Factories',
        'Chemical Processing Facilities'
      ],
      stats: {
        efficiency: '38% increase in productivity',
        cost: '29% reduction in downtime',
        satisfaction: '96% compliance rate'
      }
    }
  ];

  // Key platform benefits with icons
  const platformBenefits = [
    {
      icon: Clock,
      title: 'Time & Attendance Tracking',
      description: 'Automated clock-in/out with GPS verification, facial recognition, and comprehensive timesheet management.'
    },
    {
      icon: Calendar,
      title: 'Intelligent Scheduling',
      description: 'AI-powered shift planning that considers availability, skills, Employee laws, and business demands.'
    },
    {
      icon: Users,
      title: 'Team Management',
      description: 'Complete employee profiles, role assignments, department organization, and communication tools.'
    },
    {
      icon: BarChart3,
      title: 'Advanced Analytics',
      description: 'Real-time dashboards, Employee cost analysis, productivity reports, and predictive insights.'
    },
    {
      icon: Smartphone,
      title: 'Mobile-First Design',
      description: 'Native iOS and Android apps for employees and managers to access everything on the go.'
    },
    {
      icon: Shield,
      title: 'Compliance & Security',
      description: 'Built-in Employee law compliance, data encryption, role-based access, and audit trails.'
    }
  ];

  // Core features showcase
  const coreFeatures = [
    {
      icon: UserCheck,
      title: 'Automated Shift Assignment',
      description: 'AI algorithms automatically assign shifts based on employee preferences, skills, availability, and business requirements.',
      highlight: 'Save 10+ hours per week'
    },
    {
      icon: Bell,
      title: 'Real-Time Notifications',
      description: 'Instant push notifications for shift changes, time-off approvals, schedule updates, and important announcements.',
      highlight: 'Zero miscommunication'
    },
    {
      icon: MapPin,
      title: 'Geo-Location Verification',
      description: 'Ensure employees clock in from authorized locations with GPS tracking and geofencing technology.',
      highlight: '100% accountability'
    },
    {
      icon: FileText,
      title: 'Leave Management',
      description: 'Streamlined time-off requests, approval workflows, balance tracking, and automatic schedule adjustments.',
      highlight: 'Instant approvals'
    },
    {
      icon: DollarSign,
      title: 'Payroll Integration',
      description: 'Seamless integration with popular payroll systems for accurate, automated wage calculations and reporting.',
      highlight: 'Error-free payroll'
    },
    {
      icon: Target,
      title: 'Performance Tracking',
      description: 'Monitor attendance rates, punctuality, overtime patterns, and productivity metrics for data-driven decisions.',
      highlight: 'Actionable insights'
    }
  ];

  // Statistics and ROI
  const roiMetrics = [
    { value: '10,000+', label: 'Organizations Trust Shiftry', icon: Building2 },
    { value: '500,000+', label: 'Employees Managed Daily', icon: Users },
    { value: '35%', label: 'Average Cost Reduction', icon: TrendingUp },
    { value: '98%', label: 'Customer Satisfaction', icon: Award }
  ];

  // Testimonials
  const testimonials = [
    {
      quote: "Shiftry transformed how we manage our nursing staff across 5 facilities. The automated scheduling saved us countless hours and reduced overtime costs by 40%.",
      author: "Dr. Sarah Mitchell",
      role: "Chief Operations Officer",
      company: "MediCare Health Systems",
      industry: "Healthcare",
      avatar: "👩‍⚕️"
    },
    {
      quote: "Managing 200+ restaurant employees used to be a nightmare. Shiftry's mobile app and real-time updates have made shift management incredibly smooth.",
      author: "James Chen",
      role: "General Manager",
      company: "Sunrise Restaurant Group",
      industry: "Hospitality",
      avatar: "👨‍🍳"
    },
    {
      quote: "The ROI was immediate. We cut scheduling time by 75% and our staff loves the transparency and mobile access. Best investment we've made.",
      author: "Maria Rodriguez",
      role: "Hotel Director",
      company: "Grand Plaza Hotels",
      industry: "Hotels",
      avatar: "👩‍💼"
    }
  ];

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const scaleIn = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: { scale: 1, opacity: 1 }
  };

  return (
    <>
      <LandingNav />
      
      {/* Hero Section with Animated Gradient */}
      <section className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 text-white py-20 md:py-32 overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500/30 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-blue-400/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-center"
          >
            <motion.div variants={fadeInUp} className="inline-flex items-center bg-white/10 backdrop-blur-md px-6 py-2 rounded-full mb-6">
              <Zap className="w-5 h-5 mr-2 text-yellow-300" />
              <span className="text-sm font-semibold">Enterprise-Grade Workforce Management</span>
            </motion.div>

            <motion.h1 
              variants={fadeInUp}
              className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight"
            >
              Industry-Specific Solutions
              <br />
              <span className="bg-gradient-to-r from-blue-200 to-purple-200 bg-clip-text text-transparent">
                Tailored for Your Business
              </span>
            </motion.h1>

            <motion.p 
              variants={fadeInUp}
              className="text-xl md:text-2xl text-blue-100 mb-8 max-w-4xl mx-auto leading-relaxed"
            >
              Empower your organization with intelligent workforce management solutions designed for your industry's unique challenges. From healthcare to hospitality, manufacturing to retail—Shiftry adapts to your needs.
            </motion.p>

            <motion.div 
              variants={fadeInUp}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-blue-600 px-8 py-4 rounded-xl font-bold text-lg shadow-2xl hover:shadow-3xl transition-all flex items-center group"
              >
                Schedule a Demo
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-blue-500/20 backdrop-blur-md border-2 border-white/30 text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-blue-500/30 transition-all"
              >
                View Pricing
              </motion.button>
            </motion.div>

            {/* Trust indicators */}
            <motion.div 
              variants={fadeInUp}
              className="mt-12 flex flex-wrap justify-center items-center gap-8 text-blue-200"
            >
              <div className="flex items-center">
                <Shield className="w-5 h-5 mr-2" />
                <span className="text-sm">SOC 2 Certified</span>
              </div>
              <div className="flex items-center">
                <Globe className="w-5 h-5 mr-2" />
                <span className="text-sm">Available in 50+ Countries</span>
              </div>
              <div className="flex items-center">
                <Award className="w-5 h-5 mr-2" />
                <span className="text-sm">4.9/5 Customer Rating</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ROI Metrics Section */}
      <section className="py-16 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="grid grid-cols-2 md:grid-cols-4 gap-8"
          >
            {roiMetrics.map((metric, index) => (
              <motion.div
                key={index}
                variants={scaleIn}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                  <metric.icon className="w-8 h-8 text-blue-600" />
                </div>
                <div className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                  {metric.value}
                </div>
                <div className="text-sm text-gray-600">
                  {metric.label}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Industry Solutions Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Purpose-Built for Your Industry
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Every industry has unique workforce challenges. Our specialized solutions address your specific operational needs with precision and expertise.
            </p>
          </motion.div>

          {/* Industry Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {industries.map((industry, index) => (
              <motion.div
                key={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -10, boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)" }}
                onClick={() => setActiveIndustry(index)}
                className={`bg-white rounded-2xl shadow-xl p-8 cursor-pointer transition-all duration-300 border-2 ${
                  activeIndustry === index ? 'border-blue-500' : 'border-transparent'
                }`}
              >
                <div className={`w-16 h-16 bg-gradient-to-br ${industry.color} rounded-2xl flex items-center justify-center mb-6 shadow-lg`}>
                  <industry.icon className="w-8 h-8 text-white" strokeWidth={2} />
                </div>
                
                <div className="text-4xl mb-4">{industry.image}</div>
                
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  {industry.title}
                </h3>
                <p className="text-sm text-blue-600 font-semibold mb-4">
                  {industry.subtitle}
                </p>
                <p className="text-gray-600 leading-relaxed mb-6">
                  {industry.description}
                </p>

                {/* Quick stats */}
                <div className="grid grid-cols-1 gap-3 pt-6 border-t border-gray-100">
                  <div className="flex items-center text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                    <span className="text-gray-700">{industry.stats.efficiency}</span>
                  </div>
                  <div className="flex items-center text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                    <span className="text-gray-700">{industry.stats.cost}</span>
                  </div>
                  <div className="flex items-center text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                    <span className="text-gray-700">{industry.stats.satisfaction}</span>
                  </div>
                </div>

                <Link to={industry.route}>
                  <motion.button
                    whileHover={{ x: 5 }}
                    className="mt-6 text-blue-600 font-semibold flex items-center group"
                  >
                    Learn more
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </motion.button>
                </Link>
              </motion.div>
            ))}
          </div>

          {/* Detailed Industry View */}
          <AnimatePresence mode="wait">
            {activeIndustry !== null && (() => {
              const ActiveIcon = industries[activeIndustry].icon;
              return (
                <motion.div
                  key={activeIndustry}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="bg-white rounded-2xl shadow-2xl p-8 md:p-12"
                >
                  <div className="grid md:grid-cols-2 gap-12">
                    {/* Left: Benefits */}
                    <div>
                      <h3 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                        <ActiveIcon className="w-8 h-8 mr-3 text-blue-600" />
                        Key Benefits
                      </h3>
                    <ul className="space-y-4">
                      {industries[activeIndustry].benefits.map((benefit, idx) => (
                        <motion.li
                          key={idx}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.1 }}
                          className="flex items-start"
                        >
                          <CheckCircle2 className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700 text-lg">{benefit}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>

                  {/* Right: Use Cases */}
                  <div>
                    <h3 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                      <Briefcase className="w-8 h-8 mr-3 text-blue-600" />
                      Perfect For
                    </h3>
                    <div className="grid grid-cols-1 gap-4">
                      {industries[activeIndustry].useCases.map((useCase, idx) => (
                        <motion.div
                          key={idx}
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.1 }}
                          className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4 border border-blue-100"
                        >
                          <div className="flex items-center">
                            <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
                            <span className="text-gray-800 font-medium">{useCase}</span>
                          </div>
                        </motion.div>
                      ))}
                    </div>

                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="mt-8 w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all"
                    >
                      Get Started with {industries[activeIndustry].title}
                    </motion.button>
                  </div>
                </div>
              </motion.div>
              );
            })()}
          </AnimatePresence>
        </div>
      </section>

      {/* Platform Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Powerful Features, Seamless Experience
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A comprehensive workforce management platform that covers every aspect of employee scheduling, time tracking, and team coordination.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {platformBenefits.map((benefit, index) => (
              <motion.div
                key={index}
                variants={scaleIn}
                whileHover={{ scale: 1.05 }}
                className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all"
              >
                <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center mb-6 shadow-lg">
                  <benefit.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Core Features Showcase */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-purple-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Everything You Need in One Platform
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Streamline operations with features built for efficiency, compliance, and employee satisfaction.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {coreFeatures.map((feature, index) => (
              <motion.div
                key={index}
                variants={scaleIn}
                whileHover={{ y: -5 }}
                className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all cursor-pointer"
                onClick={() => setSelectedFeature(index)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-xs bg-yellow-400 text-gray-900 px-3 py-1 rounded-full font-bold">
                    {feature.highlight}
                  </span>
                </div>
                <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                <p className="text-blue-100 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Trusted by Industry Leaders
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Join thousands of organizations that have transformed their workforce management with Shiftry.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                variants={scaleIn}
                whileHover={{ y: -10 }}
                className="bg-white rounded-2xl shadow-xl p-8 relative"
              >
                <div className="text-5xl mb-6">{testimonial.avatar}</div>
                <div className="text-6xl text-blue-200 absolute top-8 right-8 opacity-50">"</div>
                
                <p className="text-gray-700 text-lg mb-6 italic leading-relaxed">
                  "{testimonial.quote}"
                </p>
                
                <div className="border-t pt-6">
                  <div className="font-bold text-gray-900 text-lg">{testimonial.author}</div>
                  <div className="text-blue-600 font-medium">{testimonial.role}</div>
                  <div className="text-gray-600 text-sm">{testimonial.company}</div>
                  <div className="mt-3 inline-block bg-blue-100 text-blue-600 px-3 py-1 rounded-full text-xs font-semibold">
                    {testimonial.industry}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-700 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Transform Your Workforce Management?
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Join 10,000+ organizations already saving time and reducing costs with Shiftry. Get started with a free demo tailored to your industry.
            </motion.p>
            
            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-blue-600 px-10 py-5 rounded-xl font-bold text-lg shadow-2xl hover:shadow-3xl transition-all flex items-center"
              >
                Schedule Your Free Demo
                <ArrowRight className="ml-3 w-5 h-5" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-transparent border-2 border-white text-white px-10 py-5 rounded-xl font-bold text-lg hover:bg-white/10 transition-all"
              >
                View Pricing Plans
              </motion.button>
            </motion.div>

            <motion.div variants={fadeInUp} className="mt-12 flex flex-wrap justify-center items-center gap-6 text-blue-100 text-sm">
              <div className="flex items-center">
                <CheckCircle2 className="w-5 h-5 mr-2" />
                <span>No credit card required</span>
              </div>
              <div className="flex items-center">
                <CheckCircle2 className="w-5 h-5 mr-2" />
                <span>14-day free trial</span>
              </div>
              <div className="flex items-center">
                <CheckCircle2 className="w-5 h-5 mr-2" />
                <span>Cancel anytime</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <FooterLanding />
    </>
  );
};

export default Solutions;